// =============================================================================
// Modal Component — Portal-based overlay with focus trap & Escape handling
// =============================================================================
// Replaced native <dialog> due to known cross-browser issues:
// - Unexpected `cancel` events closing the dialog during IME / autocomplete
// - Unreliable backdrop click detection with showModal()
// - Focus trap inconsistencies across browsers
// =============================================================================

import {
  type HTMLAttributes,
  type ReactNode,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';

export interface ModalProps extends Omit<HTMLAttributes<HTMLDivElement>, 'open'> {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  children?: ReactNode;
}

const sizeMaxWidths: Record<NonNullable<ModalProps['size']>, string> = {
  sm: '24rem',
  md: '32rem',
  lg: '42rem',
  xl: '56rem',
  '2xl': '72rem',
};

export function Modal({
  open,
  onClose,
  title,
  description,
  size = 'md',
  className,
  children,
  ...props
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Lock body scroll & save/restore focus
  useEffect(() => {
    if (!open) return;

    previousFocusRef.current = document.activeElement as HTMLElement | null;
    document.body.style.overflow = 'hidden';

    // Auto-focus the first focusable element inside the panel
    requestAnimationFrame(() => {
      const focusable = panelRef.current?.querySelector<HTMLElement>(
        'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable) {
        focusable.focus();
      } else {
        panelRef.current?.focus();
      }
    });

    return () => {
      document.body.style.overflow = '';
      previousFocusRef.current?.focus();
    };
  }, [open]);

  // Escape key handler
  useEffect(() => {
    if (!open) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  // Focus trap
  useEffect(() => {
    if (!open) return;

    function handleFocusTrap(e: KeyboardEvent) {
      if (e.key !== 'Tab' || !panelRef.current) return;

      const focusableElements = panelRef.current.querySelectorAll<HTMLElement>(
        'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"]), a[href]',
      );
      if (focusableElements.length === 0) return;

      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleFocusTrap);
    return () => document.removeEventListener('keydown', handleFocusTrap);
  }, [open]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose],
  );

  if (!open) return null;

  return createPortal(
    <>
      {/* Backdrop. Inline z-index (instead of Tailwind `z-50`) keeps
          the modal above app-level overlays such as the apparel
          designer toolbars at z-index 800–900. Tailwind arbitrary
          values like `z-[1000]` were unreliable across workspace
          package JIT scanning, so this stays as an inline style.
          Modals are definitionally the topmost surface — toasts,
          dropdowns, tooltips belong below. */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm animate-in fade-in-0 duration-150"
        style={{ zIndex: 1000 }}
        aria-hidden="true"
      />

      {/* Scroll container — full viewport, click outside panel = close */}
      <div
        className="fixed inset-0 overflow-y-auto"
        style={{ zIndex: 1000 }}
        onClick={handleBackdropClick}
      >
        <div className="flex min-h-full items-start justify-center p-3 sm:items-center sm:p-4">
          {/* Panel */}
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label={title}
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
            style={{ width: '100%', maxWidth: sizeMaxWidths[size] }}
            className={clsx(
              'relative rounded-xl border border-border bg-surface shadow-lg',
              'animate-in fade-in-0 zoom-in-95 duration-150',
              className,
            )}
            {...props}
          >
            <div className="p-4 sm:p-6">
              {title && (
                <div className="mb-4 pr-10">
                  <h2 className="text-lg font-semibold text-text-primary break-words">{title}</h2>
                  {description && (
                    <p className="mt-1 text-sm text-text-secondary">{description}</p>
                  )}
                </div>
              )}
              {children}
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-md text-text-tertiary hover:bg-surface-secondary hover:text-text-primary"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

Modal.displayName = 'Modal';

// ---------------------------------------------------------------------------
// Modal sub-components
// ---------------------------------------------------------------------------

export interface ModalFooterProps extends HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export function ModalFooter({ className, children, ...props }: ModalFooterProps) {
  return (
    <div
      className={clsx(
        'flex items-center justify-end gap-3 pt-4 border-t border-border mt-4',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

ModalFooter.displayName = 'ModalFooter';
